import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, Chip, Stack,
  LinearProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  CalendarMonth as CalendarIcon,
  Receipt as PayslipIcon,
  Savings as SavingsIcon,
  AssignmentTurnedIn as ApprovalsIcon,
  EventAvailable as ScheduleIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { STATUS_COLORS } from '@/theme/colorTokens';
import { formatCurrency, formatDate } from '@/utils/format';
import { useJurisdiction } from '@/hooks/useJurisdiction';
import { employeesApi, leaveApi, portalFinanceApi } from '@/api/endpoints/hr';
import apiClient from '@/api/client';
import { InfoRow } from './shared';

interface OverviewTabProps {
  user: any;
  emp: any;
  activeCompany: any;
  onNavigateTab: (tabIndex: number, subTab?: number) => void;
}

export default function OverviewTab({ user, emp, activeCompany, onNavigateTab }: OverviewTabProps) {
  const { currencyCode } = useJurisdiction();

  const { data: leaveBalances } = useQuery({
    queryKey: ['hr/leave-balances', emp?.id],
    queryFn: () => employeesApi.getLeaveBalances(emp!.id, new Date().getFullYear()).then(r => r.data),
    enabled: !!emp?.id,
  });

  const { data: leaveRequests } = useQuery({
    queryKey: ['hr/leave-requests', emp?.id],
    queryFn: () => leaveApi.list({ employee_id: emp!.id, page: 1, per_page: 10 }).then(r => r.data),
    enabled: !!emp?.id,
  });

  const { data: payslips } = useQuery({
    queryKey: ['my-payslips-overview'],
    queryFn: async () => {
      const res = await apiClient.get<{ items: any[] }>('/payroll/my-payslips?limit=3');
      return res.data.items;
    },
  });

  const { data: finances } = useQuery({
    queryKey: ['my-finances'],
    queryFn: () => portalFinanceApi.getMyFinances(),
    select: (res) => res.data,
  });

  const { data: pendingApprovals } = useQuery({
    queryKey: ['approvals-badge-count'],
    queryFn: async () => {
      try {
        const res = await apiClient.get<{ items: any[] }>('/approvals/pending');
        return res.data.items?.length || 0;
      } catch { return 0; }
    },
  });

  const totalRemaining = (leaveBalances || []).reduce((sum: number, b: any) => sum + (b.entitled_days - b.used_days), 0);
  const totalEntitled = (leaveBalances || []).reduce((sum: number, b: any) => sum + b.entitled_days, 0);
  const pendingLeave = (leaveRequests?.items || []).filter((r: any) => r.status === 'pending').length;
  const nextApproved = (leaveRequests?.items || []).find((r: any) => r.status === 'approved' && new Date(r.start_date) >= new Date());
  const lastPayslip = payslips && payslips.length > 0 ? payslips[0] : null;
  const totalSavings = (finances?.deductions || []).filter((d: any) => d.deduction_type === 'savings').reduce((s: number, d: any) => s + Number(d.accumulated_amount || 0), 0);

  return (
    <Box>
      {/* Quick Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card
            variant="outlined"
            sx={{ cursor: 'pointer', transition: 'all 0.15s', '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }, borderTop: '3px solid', borderTopColor: 'primary.main' }}
            onClick={() => onNavigateTab(2)}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', fontWeight: TYPE.semibold }}>Leave Balance</Typography>
              </Stack>
              <Typography sx={{ fontSize: TYPE['2xl'], fontWeight: TYPE.bold, color: 'primary.main' }}>
                {totalRemaining}
                <Typography component="span" sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium, color: 'text.secondary' }}> / {totalEntitled}</Typography>
              </Typography>
              {pendingLeave > 0 && (
                <Chip label={`${pendingLeave} pending`} size="small" color="warning" variant="outlined" sx={{ height: 20, mt: 0.5 }} />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card
            variant="outlined"
            sx={{ cursor: 'pointer', transition: 'all 0.15s', '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }, borderTop: '3px solid', borderTopColor: 'success.main' }}
            onClick={() => onNavigateTab(3)}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <PayslipIcon sx={{ fontSize: 18, color: 'success.main' }} />
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', fontWeight: TYPE.semibold }}>Last Net Pay</Typography>
              </Stack>
              <Typography sx={{ fontSize: TYPE['2xl'], fontWeight: TYPE.bold, color: 'success.main' }}>
                {lastPayslip ? formatCurrency(lastPayslip.net_pay, currencyCode) : '—'}
              </Typography>
              {lastPayslip && (
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', mt: 0.5 }}>
                  {new Date(lastPayslip.period_year, lastPayslip.period_month - 1).toLocaleString('default', { month: 'short', year: 'numeric' })}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card
            variant="outlined"
            sx={{ cursor: 'pointer', transition: 'all 0.15s', '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }, borderTop: '3px solid', borderTopColor: 'info.main' }}
            onClick={() => onNavigateTab(3, 1)}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <SavingsIcon sx={{ fontSize: 18, color: 'info.main' }} />
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', fontWeight: TYPE.semibold }}>Total Savings</Typography>
              </Stack>
              <Typography sx={{ fontSize: TYPE['2xl'], fontWeight: TYPE.bold, color: 'info.main' }}>
                {formatCurrency(totalSavings, currencyCode)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card
            variant="outlined"
            sx={{ cursor: 'pointer', transition: 'all 0.15s', '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }, borderTop: '3px solid', borderTopColor: 'warning.main' }}
            onClick={() => onNavigateTab(5)}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <ApprovalsIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', fontWeight: TYPE.semibold }}>Pending Approvals</Typography>
              </Stack>
              <Typography sx={{ fontSize: TYPE['2xl'], fontWeight: TYPE.bold, color: pendingApprovals ? 'warning.main' : 'text.primary' }}>
                {pendingApprovals ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card
            variant="outlined"
            sx={{ cursor: 'pointer', transition: 'all 0.15s', '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }, borderTop: '3px solid', borderTopColor: 'secondary.main' }}
            onClick={() => onNavigateTab(2)}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', fontWeight: TYPE.semibold }}>Next Leave</Typography>
              </Stack>
              <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>
                {nextApproved ? formatDate(nextApproved.start_date) : 'None scheduled'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Two-column layout: Leave Balances + Recent Payslips */}
      <Grid container spacing={2.5}>
        {/* Leave Balances */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Leave Balances</Typography>
                <Chip label="View All" size="small" variant="outlined" clickable onClick={() => onNavigateTab(2)} />
              </Stack>
              {(leaveBalances || []).length === 0 ? (
                <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', textAlign: 'center', py: 3 }}>No leave data available.</Typography>
              ) : (
                <Stack spacing={1.5}>
                  {(leaveBalances || []).slice(0, 4).map((bal: any) => {
                    const remaining = bal.entitled_days - bal.used_days;
                    const pct = bal.entitled_days > 0 ? (bal.used_days / bal.entitled_days) * 100 : 0;
                    const color = remaining >= bal.entitled_days * 0.5 ? STATUS_COLORS.success : remaining >= bal.entitled_days * 0.25 ? STATUS_COLORS.warning : STATUS_COLORS.error;
                    return (
                      <Box key={bal.leave_type_id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium }}>{bal.leave_type_name}</Typography>
                          <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold, color }}>
                            {remaining} <Typography component="span" sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>/ {bal.entitled_days}</Typography>
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(pct, 100)}
                          sx={{ height: 6, borderRadius: 3, bgcolor: `${color}20`, '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Payslips */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Recent Payslips</Typography>
                <Chip label="View All" size="small" variant="outlined" clickable onClick={() => onNavigateTab(3, 1)} />
              </Stack>
              {!payslips || payslips.length === 0 ? (
                <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', textAlign: 'center', py: 3 }}>No payslips available.</Typography>
              ) : (
                <Stack spacing={1}>
                  {payslips.slice(0, 3).map((p: any) => {
                    const monthName = new Date(p.period_year, p.period_month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                    return (
                      <Box key={p.id} sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        py: 1.25, px: 1.5, borderRadius: '8px', bgcolor: 'action.hover',
                      }}>
                        <Box>
                          <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.semibold }}>{monthName}</Typography>
                          <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                            Gross: {formatCurrency(p.gross_pay, currencyCode)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold, color: 'success.main' }}>
                            {formatCurrency(p.net_pay, currencyCode)}
                          </Typography>
                          <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>net pay</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Work Info */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 1.5 }}>Work Information</Typography>
              {emp ? (
                <>
                  <InfoRow label="Employee Ref" value={emp.employee_ref} />
                  <InfoRow label="Designation" value={emp.designation} />
                  <InfoRow label="Status" value={emp.employment_status} />
                  <InfoRow label="Date of Hire" value={formatDate(emp.date_of_hire)} />
                  <InfoRow label="Company" value={activeCompany?.company_name} />
                  <InfoRow label="Role" value={activeCompany?.role_name} />
                </>
              ) : (
                <>
                  <InfoRow label="Role" value={activeCompany?.role_name} />
                  <InfoRow label="Company" value={activeCompany?.company_name} />
                  <InfoRow label="Email" value={user?.email} />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Leave Requests */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Recent Leave Requests</Typography>
                <Chip label="View All" size="small" variant="outlined" clickable onClick={() => onNavigateTab(2)} />
              </Stack>
              {(leaveRequests?.items || []).length === 0 ? (
                <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', textAlign: 'center', py: 3 }}>No leave requests yet.</Typography>
              ) : (
                <Stack spacing={1}>
                  {(leaveRequests?.items || []).slice(0, 4).map((req: any) => {
                    const statusColor = req.status === 'approved' ? 'success' : req.status === 'pending' ? 'warning' : req.status === 'rejected' ? 'error' : 'default';
                    return (
                      <Box key={req.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box>
                          <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium }}>{req.leave_type_name}</Typography>
                          <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                            {formatDate(req.start_date)} · {req.days} day{req.days !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <Chip label={req.status} size="small" color={statusColor as any} variant="outlined" sx={{ height: 22 }} />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
